import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Calendar, Clock, ArrowRight, Sparkles, BookOpen, Lightbulb, Target, TrendingUp, Users, Heart, Zap, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL || 'https://learnwithzeenat.com';

function TeacherTips() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  const categoryIcons = {
    'classroom_ideas': Lightbulb,
    'assessment': Target,
    'differentiation': Users,
    'ai_for_teachers': Sparkles,
    'parent_tips': Heart,
    'behaviour_management': Award,
    'student_motivation': TrendingUp,
    'inclusive_education': Users,
    'creative_teaching': Zap,
    'teacher_productivity': BookOpen
  };

  const categories = [
    { value: '', label: 'All Articles', icon: BookOpen },
    { value: 'classroom_ideas', label: 'Classroom Ideas', icon: Lightbulb },
    { value: 'ai_for_teachers', label: 'AI for Teachers', icon: Sparkles },
    { value: 'assessment', label: 'Assessment', icon: Target },
    { value: 'differentiation', label: 'Differentiation', icon: Users },
    { value: 'behaviour_management', label: 'Behaviour Management', icon: Award },
    { value: 'student_motivation', label: 'Student Motivation', icon: TrendingUp },
    { value: 'inclusive_education', label: 'Inclusive Education', icon: Heart },
    { value: 'creative_teaching', label: 'Creative Teaching', icon: Zap },
    { value: 'teacher_productivity', label: 'Teacher Productivity', icon: BookOpen }
  ];

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API}/teaching-tips?${params.toString()}`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const featuredArticles = articles.filter(a => a.is_featured);
  const regularArticles = articles.filter(a => !a.is_featured);

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Teacher Tips & Teaching Resources | Learn with Zeenat</title>
        <meta name="description" content="Discover practical teaching strategies, creative classroom ideas, and AI tips for teachers. Free resources to make teaching more effective and enjoyable." />
        <link rel="canonical" href={`${SITE_URL}/teaching-tips`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/teaching-tips`} />
        <meta property="og:title" content="Teacher Tips & Teaching Resources" />
        <meta property="og:description" content="Practical strategies, creative ideas, and AI tips to make teaching more effective and enjoyable" />
        <meta property="og:image" content={`${SITE_URL}/og-teaching-tips.png`} />
        <meta property="og:site_name" content="Learn with Zeenat" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Teacher Tips & Teaching Resources" />
        <meta name="twitter:description" content="Practical strategies, creative ideas, and AI tips to make teaching more effective and enjoyable" />
        <meta name="twitter:image" content={`${SITE_URL}/og-teaching-tips.png`} />
        
        <meta name="keywords" content="teaching tips, classroom ideas, teacher resources, AI for teachers, teaching strategies, educational resources" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.1em] uppercase font-semibold text-blue-600 mb-3">
              Teacher Tips & Ideas
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-bold text-slate-900 mb-6">
              Resources for Better Teaching
            </h1>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Practical strategies, creative ideas, and AI tips to make teaching more effective and enjoyable
            </p>
          </div>
          
          {/* Categories */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((cat, index) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>
      
      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading articles...</p>
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20">
          <Lightbulb className="w-20 h-20 text-slate-300 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-slate-900 mb-3">No articles yet</h3>
          <p className="text-lg text-slate-600">Check back soon for teaching tips and ideas!</p>
        </div>
      ) : (
        <>
          {/* Featured Articles */}
          {featuredArticles.length > 0 && (
            <section className="py-16 px-6 sm:px-8 lg:px-12">
              <div className="max-w-7xl mx-auto">
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                  {featuredArticles.map((article) => {
                    const CategoryIcon = categoryIcons[article.category] || Lightbulb;
                    return (
                      <Link
                        key={article.article_id}
                        to={`/teacher-tips/${article.article_id}`}
                        className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
                      >
                        {article.featured_image && (
                          <div className="aspect-video overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50">
                            <img 
                              src={article.featured_image} 
                              alt={article.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <div className="p-8">
                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                              <CategoryIcon className="w-3.5 h-3.5" />
                              {formatCategory(article.category)}
                            </span>
                            <span className="text-sm text-slate-500 inline-flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {article.reading_time} min read
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold text-slate-900 mb-3 line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-base text-slate-600 mb-4 line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">Z</span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{article.author}</p>
                                <p className="text-xs text-slate-500">{formatDate(article.published_at)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-blue-600 font-medium">
                              Read Article
                              <ArrowRight className="w-4 h-4" strokeWidth={2} />
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
          
          {/* All Articles */}
          {regularArticles.length > 0 && (
            <section className="py-8 px-6 sm:px-8 lg:px-12">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-2xl font-bold text-slate-900 mb-8">
                  {featuredArticles.length > 0 ? 'Latest Articles' : 'All Articles'}
                </h2>
                <div className="grid md:grid-cols-3 gap-8">
                  {regularArticles.map((article) => {
                    const CategoryIcon = categoryIcons[article.category] || Lightbulb;
                    return (
                      <Link
                        key={article.article_id}
                        to={`/teacher-tips/${article.article_id}`}
                        className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
                      >
                        {article.featured_image ? (
                          <div className="aspect-video overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50">
                            <img 
                              src={article.featured_image} 
                              alt={article.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        ) : (
                          <div className="aspect-video bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
                            <CategoryIcon className="w-16 h-16 text-blue-400 opacity-50" strokeWidth={1.5} />
                          </div>
                        )}
                        <div className="p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg inline-flex items-center gap-1">
                              <CategoryIcon className="w-3 h-3" />
                              {formatCategory(article.category)}
                            </span>
                            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {article.reading_time} min
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {formatDate(article.published_at)}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </>
      )}
      
      {/* Newsletter CTA */}
      <section className="py-16 px-6 sm:px-8 lg:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Never Miss a Teaching Tip
            </h3>
            <p className="text-base text-slate-600 mb-6 max-w-md mx-auto">
              Get the latest articles, resources, and AI tips delivered to your inbox every week.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              />
              <button
                className="bg-blue-600 text-white rounded-xl px-6 py-3 font-medium shadow-sm hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 whitespace-nowrap"
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default TeacherTips;
